import { useState } from "react";
import { BookSection } from "@/mocks/bookData";
import IllustrationLightbox from "./IllustrationLightbox";

interface BookSectionProps {
  section: BookSection;
  isFirst: boolean;
  isLast: boolean;
  onPrev: () => void;
  onNext: () => void;
  onTOC: () => void;
}

const renderParagraph = (
  para: string,
  i: number,
  onImageClick: (src: string) => void
) => {
  if (para.startsWith("[[ILLUS:") && para.endsWith("]]")) {
    const src = para.slice(8, -2);
    return (
      <div
        key={i}
        className="my-14 group cursor-zoom-in"
        onClick={() => onImageClick(src)}
        role="button"
        aria-label="View illustration full screen"
      >
        <div className="w-20 h-px bg-[#C8922A]/25 mx-auto mb-10" />
        <div className="w-full overflow-hidden border border-[#1A1A1A] relative">
          <img
            src={src}
            alt="Illustration"
            className="w-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.02]"
            style={{ maxHeight: "560px" }}
          />
          {/* Hover overlay hint */}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 w-10 h-10 flex items-center justify-center bg-black/60 border border-white/20">
              <i className="ri-fullscreen-line text-white/80 text-lg" />
            </div>
          </div>
        </div>
        <div className="w-20 h-px bg-[#C8922A]/25 mx-auto mt-10" />
      </div>
    );
  }

  if (para.startsWith("## ")) {
    return (
      <h3
        key={i}
        className="font-['Special_Elite'] text-white text-xl md:text-2xl mt-12 mb-3 pt-10 border-t border-[#141414] first:mt-0 first:pt-0 first:border-t-0"
      >
        {para.slice(3)}
      </h3>
    );
  }

  if (para.startsWith("SPEAKER:")) {
    const name = para.slice(8);
    return (
      <div
        key={i}
        className="font-['Share_Tech_Mono'] text-[#C8922A] text-xs tracking-[0.25em] uppercase mt-8 mb-1"
      >
        — {name}
      </div>
    );
  }

  if (para.startsWith("> ")) {
    return (
      <blockquote
        key={i}
        className="border-l-2 border-[#C8922A]/50 pl-5 py-0.5 my-1"
      >
        <p className="font-['Special_Elite'] text-[#D0C4A8] text-base md:text-lg leading-[1.85] italic">
          {para.slice(2)}
        </p>
      </blockquote>
    );
  }

  if (para.startsWith("!! ")) {
    return (
      <div
        key={i}
        className="my-8 px-6 py-5 border-l-2 border-[#C8922A] bg-[#C8922A]/5"
      >
        <p className="font-['Special_Elite'] text-[#C8922A] text-base md:text-lg leading-[1.85] italic">
          {para.slice(3)}
        </p>
      </div>
    );
  }

  return (
    <p
      key={i}
      className="font-['Rajdhani'] text-[#AAAAAA] leading-[1.95] text-base md:text-[1.1rem]"
    >
      {para}
    </p>
  );
};

export default function BookSectionComp({
  section,
  isFirst,
  isLast,
  onPrev,
  onNext,
  onTOC,
}: BookSectionProps) {
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);

  return (
    <section
      id={section.id}
      className="w-full px-6 py-24 border-b border-[#111] scroll-mt-20"
    >
      <div className="max-w-3xl mx-auto">
        {/* Chapter Header */}
        <div className="mb-14">
          <div className="flex items-center gap-4 mb-4">
            <span className="font-['Share_Tech_Mono'] text-[#C8922A] text-xs tracking-[0.4em] uppercase">
              {section.number}
            </span>
            <div className="flex-1 h-px bg-[#1A1A1A]" />
            {section.subChapters.length > 0 && (
              <span className="font-['Share_Tech_Mono'] text-[#333] text-xs tracking-[0.15em]">
                {section.subChapters.join(" · ")}
              </span>
            )}
          </div>

          <h2 className="font-['Special_Elite'] text-white text-4xl md:text-5xl leading-tight border-b border-[#1A1A1A] pb-6">
            {section.title}
          </h2>
        </div>

        {/* Content */}
        <article className="space-y-4">
          {section.content
            .split("\n\n")
            .map((para, i) => renderParagraph(para.trim(), i, setLightboxSrc))}
        </article>

        {/* Navigation Arrows */}
        <div className="flex items-center justify-between mt-16 pt-8 border-t border-[#1A1A1A]">
          <button
            disabled={isFirst}
            onClick={onPrev}
            className={`flex items-center gap-2 font-['Share_Tech_Mono'] text-xs tracking-[0.15em] uppercase px-4 py-2 border transition-all duration-300 cursor-pointer whitespace-nowrap ${
              isFirst
                ? "border-[#1A1A1A] text-[#222] cursor-not-allowed"
                : "border-[#2C2C2C] text-[#555] hover:text-[#C8922A] hover:border-[#C8922A]/40"
            }`}
          >
            <i className="ri-arrow-left-line" />
            Previous
          </button>

          <button
            onClick={onTOC}
            className="flex items-center gap-2 font-['Share_Tech_Mono'] text-[#333] hover:text-[#C8922A] text-xs tracking-[0.15em] uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-list-unordered text-sm" />
            Contents
          </button>

          {isLast ? (
            <button
              onClick={onTOC}
              className="flex items-center gap-2 font-['Share_Tech_Mono'] text-xs tracking-[0.15em] uppercase border border-[#C8922A]/40 text-[#C8922A] hover:bg-[#C8922A]/10 px-4 py-2 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-check-line" />
              The End
            </button>
          ) : (
            <button
              onClick={onNext}
              className="flex items-center gap-2 font-['Share_Tech_Mono'] text-xs tracking-[0.15em] uppercase border border-[#C8922A]/40 text-[#C8922A] hover:bg-[#C8922A]/10 hover:border-[#C8922A] px-4 py-2 transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Next
              <i className="ri-arrow-right-line" />
            </button>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxSrc && (
        <IllustrationLightbox
          src={lightboxSrc}
          onClose={() => setLightboxSrc(null)}
        />
      )}
    </section>
  );
}
